import Bugsnag from "@bugsnag/js";

import config from "../../config";

const GOOGLE_API_SCRIPT_URL = "https://apis.google.com/js/api.js";
const GOOGLE_IDENTITY_SCRIPT_URL = "https://accounts.google.com/gsi/client";
const GOOGLE_API_READY_TIMEOUT_MS = 10_000;
const DRIVE_FILE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const DRIVE_ABOUT_URL =
  "https://www.googleapis.com/drive/v3/about?fields=user(emailAddress)";
const TOKEN_EXPIRY_MARGIN_MS = 60_000;

type GoogleDrivePickerDoc = {
  id: string;
  name: string;
  mimeType?: string;
};

type GoogleDrivePickerCallbackData = {
  action?: string;
  docs?: GoogleDrivePickerDoc[];
};

type GoogleTokenResponse = {
  access_token?: string;
  expires_in?: number | string;
  scope?: string;
  error?: string;
  error_description?: string;
};

type GoogleTokenClientError = {
  type?: string;
  message?: string;
};

type GoogleTokenClientConfig = {
  client_id: string;
  scope: string;
  include_granted_scopes: boolean;
  callback: (response: GoogleTokenResponse) => void;
  error_callback: (error: GoogleTokenClientError) => void;
};

type GoogleTokenRequestOverrides = {
  prompt: string;
  include_granted_scopes: boolean;
  login_hint?: string;
};

type GoogleTokenClient = {
  requestAccessToken(overrides?: GoogleTokenRequestOverrides): void;
};

type GoogleDriveDocsView = {
  setParent(parent: string): GoogleDriveDocsView;
  setMimeTypes(mimeTypes: string): GoogleDriveDocsView;
  setIncludeFolders(includeFolders: boolean): GoogleDriveDocsView;
  setEnableDrives(enabled: boolean): GoogleDriveDocsView;
  setOwnedByMe(ownedByMe: boolean): GoogleDriveDocsView;
  setLabel?(label: string): GoogleDriveDocsView;
};

type GoogleDrivePickerBuilder = {
  enableFeature(feature: string): GoogleDrivePickerBuilder;
  setAppId(appId: string): GoogleDrivePickerBuilder;
  setOAuthToken(token: string): GoogleDrivePickerBuilder;
  setMaxItems(maxItems: number): GoogleDrivePickerBuilder;
  addView(view: GoogleDriveDocsView): GoogleDrivePickerBuilder;
  setCallback(
    callback: (data: GoogleDrivePickerCallbackData) => void
  ): GoogleDrivePickerBuilder;
  build(): {
    setVisible(isVisible: boolean): void;
  };
};

type GoogleDrivePickerNamespace = {
  DocsView: new (viewId: string) => GoogleDriveDocsView;
  PickerBuilder: new () => GoogleDrivePickerBuilder;
  ViewId: { DOCS: string };
  Feature: { MULTISELECT_ENABLED: string; SUPPORT_DRIVES?: string };
  Action: { PICKED: string; CANCEL: string };
};

type GoogleApiWindow = Window & {
  gapi?: {
    load(api: string, callback?: () => void): void;
  };
  google?: {
    accounts?: {
      oauth2?: {
        initTokenClient(config: GoogleTokenClientConfig): GoogleTokenClient;
      };
    };
    picker?: GoogleDrivePickerNamespace;
  };
};

export type GDriveFile = {
  name: string;
  mimeType?: string;
  getImage(): Promise<Blob>;
};

export type GoogleDrivePickerErrorKind =
  "popup_closed" | "popup_blocked" | "access_denied" | "unknown";

export class GoogleDrivePickerError extends Error {
  readonly kind: GoogleDrivePickerErrorKind;

  constructor(kind: GoogleDrivePickerErrorKind, message: string) {
    super(message);
    this.name = "GoogleDrivePickerError";
    this.kind = kind;
  }
}

type HeldToken = { accessToken: string; expiresAt: number };

export type GoogleDriveSessionSnapshot = {
  active: boolean;
  email: string | null;
};

const INACTIVE_SESSION: GoogleDriveSessionSnapshot = {
  active: false,
  email: null,
};

let heldToken: HeldToken | null = null;
let heldTokenEmail: { accessToken: string; email: string | null } | null = null;
let emailRequest: {
  accessToken: string;
  email: Promise<string | null>;
} | null = null;
let inFlightSelection: Promise<GDriveFile[]> | null = null;
let sessionSnapshot = INACTIVE_SESSION;
const sessionListeners = new Set<() => void>();

function getGoogleWindow(): GoogleApiWindow {
  return window as GoogleApiWindow;
}

function getValidAccessToken(): string | null {
  if (
    !heldToken ||
    Date.now() >= heldToken.expiresAt - TOKEN_EXPIRY_MARGIN_MS
  ) {
    return null;
  }

  return heldToken.accessToken;
}

export function hasGoogleDriveSession(): boolean {
  return getValidAccessToken() !== null;
}

function getHeldTokenEmail(): string | null {
  return heldToken && heldTokenEmail?.accessToken === heldToken.accessToken
    ? heldTokenEmail.email
    : null;
}

function refreshSessionSnapshot(): boolean {
  const active = getValidAccessToken() !== null;
  const email = active ? getHeldTokenEmail() : null;

  if (active === sessionSnapshot.active && email === sessionSnapshot.email) {
    return false;
  }

  sessionSnapshot = active ? { active, email } : INACTIVE_SESSION;
  return true;
}

function notifySessionListeners() {
  for (const listener of sessionListeners) {
    listener();
  }
}

function publishSession() {
  if (refreshSessionSnapshot()) {
    notifySessionListeners();
  }
}

export function subscribeGoogleDriveSession(listener: () => void) {
  sessionListeners.add(listener);

  return () => {
    sessionListeners.delete(listener);
  };
}

export function getGoogleDriveSessionSnapshot(): GoogleDriveSessionSnapshot {
  if (refreshSessionSnapshot()) {
    // Read during render, so tell other subscribers after it.
    queueMicrotask(notifySessionListeners);
  }

  return sessionSnapshot;
}

const insertedScripts = new Map<string, Promise<void>>();

function loadScript(src: string): Promise<void> {
  const inserted = insertedScripts.get(src);

  if (inserted) {
    return inserted;
  }

  if (document.querySelector(`script[src="${src}"]`)) {
    return Promise.resolve();
  }

  const loading = new Promise<void>((resolve, reject) => {
    const script = document.createElement("script");
    script.addEventListener("load", () => resolve(), { once: true });
    script.addEventListener(
      "error",
      () => {
        insertedScripts.delete(src);
        script.remove();
        reject(new Error(`Failed to load Google Drive script: ${src}`));
      },
      { once: true }
    );
    script.src = src;
    script.async = true;
    script.defer = true;
    document.head.append(script);
  });

  insertedScripts.set(src, loading);
  return loading;
}

function waitUntil(isReady: () => boolean, unavailableMessage: string) {
  return new Promise<void>((resolve, reject) => {
    const deadline = Date.now() + GOOGLE_API_READY_TIMEOUT_MS;
    const check = () => {
      if (isReady()) {
        resolve();
        return;
      }

      if (Date.now() >= deadline) {
        reject(new Error(unavailableMessage));
        return;
      }

      window.setTimeout(check, 50);
    };

    check();
  });
}

const isIdentityServicesReady = () =>
  Boolean(getGoogleWindow().google?.accounts?.oauth2?.initTokenClient);

const isGoogleApiReady = () => Boolean(getGoogleWindow().gapi?.load);

async function ensureIdentityServicesReady() {
  if (isIdentityServicesReady()) {
    return;
  }

  await loadScript(GOOGLE_IDENTITY_SCRIPT_URL);
  await waitUntil(
    isIdentityServicesReady,
    "Google Drive sign-in is unavailable"
  );
}

async function ensurePickerReady() {
  if (getGoogleWindow().google?.picker) {
    return;
  }

  if (!isGoogleApiReady()) {
    await loadScript(GOOGLE_API_SCRIPT_URL);
    await waitUntil(isGoogleApiReady, "Google Drive picker is unavailable");
  }

  await new Promise<void>((resolve, reject) => {
    try {
      getGoogleWindow().gapi?.load("picker", () => resolve());
    } catch (error) {
      reject(error);
    }
  });
}

function authorizationFailed(
  googleMessage: string,
  metadata: Record<string, unknown>
) {
  const error = new GoogleDrivePickerError(
    "unknown",
    `Google Drive authorization failed: ${googleMessage}`
  );

  Bugsnag.notify(error, (event) => {
    event.addMetadata("googleDrive", metadata);
  });

  return error;
}

function toTokenResponseError(response: GoogleTokenResponse) {
  if (response.error === "access_denied") {
    return new GoogleDrivePickerError(
      "access_denied",
      "Google Drive access wasn't granted."
    );
  }

  return authorizationFailed(
    response.error_description ||
      response.error ||
      "Google returned no access token",
    {
      error: response.error,
      error_description: response.error_description,
    }
  );
}

function toTokenClientError(clientError: GoogleTokenClientError) {
  if (clientError.type === "popup_closed") {
    return new GoogleDrivePickerError(
      "popup_closed",
      "Google Drive sign-in was closed."
    );
  }

  if (clientError.type === "popup_failed_to_open") {
    return new GoogleDrivePickerError(
      "popup_blocked",
      "Allow pop-ups for Sked Social to connect Google Drive."
    );
  }

  return authorizationFailed(
    clientError.message || clientError.type || "unknown error",
    { error: clientError.type, error_description: clientError.message }
  );
}

function requestAccessToken(overrides: GoogleTokenRequestOverrides) {
  return new Promise<HeldToken>((resolve, reject) => {
    const oauth2 = getGoogleWindow().google?.accounts?.oauth2;

    if (!oauth2) {
      reject(new Error("Google Drive sign-in is unavailable"));
      return;
    }

    const tokenClient = oauth2.initTokenClient({
      client_id: config.googleDriveClientId,
      scope: DRIVE_FILE_SCOPE,
      // Prior YouTube grants on the same Google project cannot be combined with drive.file.
      include_granted_scopes: false,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(toTokenResponseError(response));
          return;
        }

        if (
          response.scope &&
          !response.scope.split(" ").includes(DRIVE_FILE_SCOPE)
        ) {
          reject(toTokenResponseError({ error: "access_denied" }));
          return;
        }

        const expiresInSeconds = Number(response.expires_in);
        resolve({
          accessToken: response.access_token,
          expiresAt: Number.isFinite(expiresInSeconds)
            ? Date.now() + expiresInSeconds * 1000
            : 0,
        });
      },
      error_callback: (clientError) => {
        reject(toTokenClientError(clientError));
      },
    });

    tokenClient.requestAccessToken(overrides);
  });
}

async function readGoogleErrorMessage(response: Response) {
  const fallback = response.statusText || `status ${response.status}`;

  try {
    const body = (await response.json()) as {
      error?: { message?: unknown } | string;
    };
    const message =
      typeof body.error === "string" ? body.error : body.error?.message;

    return typeof message === "string" && message ? message : fallback;
  } catch {
    return fallback;
  }
}

async function fetchDriveDoc(
  doc: GoogleDrivePickerDoc,
  accessToken: string
): Promise<Blob> {
  const failure = (detail: string) =>
    new Error(`Couldn't download ${doc.name} from Google Drive: ${detail}`);

  let response: Response;

  try {
    response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media&supportsAllDrives=true`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
  } catch (error) {
    throw failure(error instanceof Error ? error.message : String(error));
  }

  if (!response.ok) {
    throw failure(await readGoogleErrorMessage(response));
  }

  return response.blob();
}

async function fetchAccountEmail(accessToken: string) {
  try {
    const response = await fetch(DRIVE_ABOUT_URL, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (!response.ok) {
      return null;
    }

    const body = (await response.json()) as {
      user?: { emailAddress?: unknown };
    };
    const email = body.user?.emailAddress;

    return typeof email === "string" && email ? email : null;
  } catch {
    return null;
  }
}

export function getGoogleDriveAccountEmail(): Promise<string | null> {
  const accessToken = getValidAccessToken();

  if (!accessToken) {
    return Promise.resolve(null);
  }

  if (heldTokenEmail?.accessToken === accessToken) {
    return Promise.resolve(heldTokenEmail.email);
  }

  if (emailRequest?.accessToken !== accessToken) {
    emailRequest = {
      accessToken,
      email: fetchAccountEmail(accessToken).then((email) => {
        if (heldToken?.accessToken !== accessToken) {
          return null;
        }

        heldTokenEmail = { accessToken, email };
        publishSession();
        return email;
      }),
    };
  }

  return emailRequest.email;
}

function openPicker(
  accessToken: string,
  { limit, mimeTypes }: { limit?: number; mimeTypes?: string }
) {
  return new Promise<GDriveFile[]>((resolve, reject) => {
    const picker = getGoogleWindow().google?.picker;

    if (!picker) {
      reject(new Error("Google Drive picker is unavailable"));
      return;
    }

    const { DocsView, ViewId, Feature, Action } = picker;
    const configureView = (view: GoogleDriveDocsView, label: string) => {
      if (mimeTypes) {
        view.setMimeTypes(mimeTypes);
      }

      if (typeof view.setLabel === "function") {
        view.setLabel(label);
      }

      return view;
    };

    const builder = new picker.PickerBuilder()
      .enableFeature(Feature.MULTISELECT_ENABLED)
      .setAppId(config.googleDriveAppId)
      .setOAuthToken(accessToken)
      .addView(
        configureView(
          new DocsView(ViewId.DOCS).setParent("root").setIncludeFolders(true),
          "My Drive"
        )
      )
      .addView(
        configureView(
          new DocsView(ViewId.DOCS)
            .setEnableDrives(true)
            .setIncludeFolders(true),
          "Shared drives"
        )
      )
      .addView(
        configureView(
          new DocsView(ViewId.DOCS).setOwnedByMe(false),
          "Shared with me"
        )
      )
      .setCallback((data) => {
        if (data.action === Action.CANCEL) {
          resolve([]);
          return;
        }

        if (data.action !== Action.PICKED) {
          return;
        }

        resolve(
          (data.docs ?? []).map((doc) => ({
            name: doc.name,
            mimeType: doc.mimeType,
            getImage: () => fetchDriveDoc(doc, accessToken),
          }))
        );
      });

    if (Feature.SUPPORT_DRIVES) {
      builder.enableFeature(Feature.SUPPORT_DRIVES);
    }

    if (limit !== undefined && Number.isFinite(limit) && limit > 0) {
      builder.setMaxItems(limit);
    }

    builder.build().setVisible(true);
  });
}

type SelectFromGoogleDriveOptions = {
  limit?: number;
  switchAccount?: boolean;
  mimeTypes?: string;
};

async function obtainAccessToken(switchAccount: boolean) {
  const validAccessToken = switchAccount ? null : getValidAccessToken();

  if (validAccessToken) {
    return validAccessToken;
  }

  const wasSignInReady = isIdentityServicesReady();

  if (!wasSignInReady) {
    await ensureIdentityServicesReady();
  }

  const loginHint = switchAccount ? null : getHeldTokenEmail();
  let token: HeldToken;

  try {
    token = await requestAccessToken(
      loginHint
        ? { prompt: "", include_granted_scopes: false, login_hint: loginHint }
        : { prompt: "select_account", include_granted_scopes: false }
    );
  } catch (error) {
    if (
      !wasSignInReady &&
      error instanceof GoogleDrivePickerError &&
      error.kind === "popup_blocked"
    ) {
      throw new GoogleDrivePickerError(
        "popup_blocked",
        "Google Drive finished loading. Please click the button again."
      );
    }

    throw error;
  }

  heldToken = token;
  publishSession();

  return token.accessToken;
}

async function runSelection({
  limit,
  switchAccount = false,
  mimeTypes,
}: SelectFromGoogleDriveOptions) {
  const accessToken = await obtainAccessToken(switchAccount);
  await ensurePickerReady();

  return openPicker(accessToken, { limit, mimeTypes });
}

export function selectFileFromGoogleDrive(
  options: SelectFromGoogleDriveOptions = {}
): Promise<GDriveFile[]> {
  if (!inFlightSelection) {
    inFlightSelection = runSelection(options).finally(() => {
      inFlightSelection = null;
    });
  }

  return inFlightSelection;
}

export function preloadGoogleDrive(): void {
  Promise.all([ensureIdentityServicesReady(), ensurePickerReady()]).catch(
    () => null
  );
}
